import React, { useMemo, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, Button, Form, Row, Col, Alert, Spinner } from 'react-bootstrap';
import { FiDownload, FiArrowLeft, FiAlertTriangle } from 'react-icons/fi';
import * as XLSX from 'xlsx';
import { getActivityClaimsFullList } from '../../services/api';
import { flattenActivityClaimsResponseForExcel, TRIPS_CLAIMS_EXPORT_COLUMNS } from '../../utils/tripsClaimsExcelRows';
import { handleApiError } from '../../utils/errorHandler';
import 'bootstrap/dist/css/bootstrap.css';
import './AdminTripsClaims.css';

const EMPTY_EXPORT_FILTERS = {
    reservation_number: '',
    policy_code: '',
    client_email: '',
    activity_end_date_from: '',
    activity_end_date_to: '',
    claim_code: '',
    date_of_claim: '',
    company_name: '',
};

const convertDateToDDMMYYYY = (dateStr) => {
    if (!dateStr) return '';
    if (dateStr.includes('/')) return dateStr;
    if (dateStr.includes('-')) {
        const [year, month, day] = dateStr.split('-');
        return `${day}/${month}/${year}`;
    }
    return dateStr;
};

const buildExportParams = (activeFilters) => {
    const params = {};
    Object.keys(activeFilters).forEach((key) => {
        if (activeFilters[key] && String(activeFilters[key]).trim() !== '') {
            if (key === 'activity_end_date_from' || key === 'activity_end_date_to') {
                params[key] = convertDateToDDMMYYYY(String(activeFilters[key]).trim());
            } else {
                params[key] = String(activeFilters[key]).trim();
            }
        }
    });
    return params;
};

const AdminActivityClaimsExport = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const initial = useMemo(
        () => (location.state?.filters && typeof location.state.filters === 'object' ? location.state.filters : {}),
        [location.state]
    );

    const [filters, setFilters] = useState(() => {
        const base = { ...EMPTY_EXPORT_FILTERS };
        Object.keys(base).forEach((k) => {
            if (initial[k] != null && String(initial[k]).trim() !== '') {
                base[k] = String(initial[k]).trim();
            }
        });
        return base;
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFilters((prev) => ({ ...prev, [name]: value }));
    };

    const handleClear = () => {
        setFilters({ ...EMPTY_EXPORT_FILTERS });
        setError(null);
    };

    const handleExport = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = buildExportParams(filters);
            const data = await getActivityClaimsFullList(params);
            const rows = flattenActivityClaimsResponseForExcel(data);
            if (!rows.length) {
                setError('Aucune ligne à exporter pour ces filtres.');
                return;
            }
            const worksheet = XLSX.utils.json_to_sheet(rows, { header: TRIPS_CLAIMS_EXPORT_COLUMNS });
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Claims');
            const stamp = new Date().toISOString().slice(0, 19).replace(/[-:T]/g, '').slice(0, 14);
            XLSX.writeFile(workbook, `activity_claims_export_${stamp}.xlsx`);
        } catch (err) {
            setError(handleApiError(err, 'Erreur lors de l\'export des sinistres'));
        } finally {
            setLoading(false);
        }
    }, [filters]);

    return (
        <div className="dashboard-page admin-trips-claims-page">
            <Card>
                <Card.Body>
                    <div className="page-header mb-3 d-flex flex-wrap justify-content-between align-items-start gap-3">
                        <div>
                            <h2 className="page-title mb-1">
                                <FiAlertTriangle className="me-2" />
                                Export Excel — Sinistres activités
                            </h2>
                            <p className="text-muted mb-0">
                                Données issues de <code>GET /api/claims</code> (filtres optionnels), fichier{' '}
                                <strong>.xlsx</strong> généré côté navigateur — mêmes colonnes que l&apos;export séjours (
                                <code>GET /api/trips/claims</code>).
                            </p>
                        </div>
                        <Button variant="outline-secondary" className="d-flex align-items-center" onClick={() => navigate('/activity/claims')}>
                            <FiArrowLeft className="me-2" />
                            Retour à la liste
                        </Button>
                    </div>

                    {error && (
                        <Alert variant="danger" className="mb-3" dismissible onClose={() => setError(null)}>
                            {error}
                        </Alert>
                    )}

                    <Alert variant="info" className="mb-3">
                        Les dates de fin d&apos;activité (filtres) sont envoyées au format <strong>DD/MM/YYYY</strong>, comme pour les séjours.
                        Les volumes importants peuvent prendre jusqu&apos;à 2 minutes.
                    </Alert>

                    <Card className="mb-3">
                        <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                            <h6 className="mb-0">Filtres d&apos;export (tous optionnels)</h6>
                            <Button variant="outline-secondary" size="sm" onClick={handleClear} type="button" disabled={loading}>
                                Réinitialiser
                            </Button>
                        </Card.Header>
                        <Card.Body>
                            <Row>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Code police</Form.Label>
                                        <Form.Control type="text" name="policy_code" value={filters.policy_code} onChange={handleChange} placeholder="Ex: 301826051" />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Numéro de réservation</Form.Label>
                                        <Form.Control type="text" name="reservation_number" value={filters.reservation_number} onChange={handleChange} />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Email client</Form.Label>
                                        <Form.Control type="email" name="client_email" value={filters.client_email} onChange={handleChange} />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Date fin activité (de)</Form.Label>
                                        <Form.Control type="date" name="activity_end_date_from" value={filters.activity_end_date_from} onChange={handleChange} />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Date fin activité (à)</Form.Label>
                                        <Form.Control type="date" name="activity_end_date_to" value={filters.activity_end_date_to} onChange={handleChange} />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Code sinistre</Form.Label>
                                        <Form.Control type="text" name="claim_code" value={filters.claim_code} onChange={handleChange} />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Date du sinistre</Form.Label>
                                        <Form.Control type="date" name="date_of_claim" value={filters.date_of_claim} onChange={handleChange} />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Partenaire (company name)</Form.Label>
                                        <Form.Control type="text" name="company_name" value={filters.company_name} onChange={handleChange} placeholder="Ex: green river" />
                                    </Form.Group>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    <div className="d-flex flex-wrap gap-2">
                        <Button variant="primary" className="d-flex align-items-center" onClick={handleExport} disabled={loading}>
                            {loading ? (
                                <>
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    Génération…
                                </>
                            ) : (
                                <>
                                    <FiDownload className="me-2" />
                                    Télécharger Excel
                                </>
                            )}
                        </Button>
                    </div>
                </Card.Body>
            </Card>
        </div>
    );
};

export default AdminActivityClaimsExport;
